import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  CategoryBarChart,
  ChartCard,
  DailySeriesChart,
  formatCount,
  formatCredits,
  formatUsd,
  CHART_SEMANTIC,
  PAGE,
  MONEY,
} from '@kioskgaming/ui';
import CreditPurchaseTrigger from '../credits/CreditPurchaseTrigger';
import GaugeChart from './GaugeChart';
import HeadlineCard from './HeadlineCard';
import MetricStrip from './MetricStrip';
import { cashSettlementView } from './chartHelpers';
import type { DashboardV2Period, SuperAgentDashboardOverviewData } from '../../services/api';

type Props = {
  period: DashboardV2Period;
  costRate: number;
  data: SuperAgentDashboardOverviewData | null;
  isLoading: boolean;
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2">
    <h2 className="text-xs font-semibold uppercase tracking-wide text-blue-900/70">{title}</h2>
    {children}
  </section>
);

export default function OverviewTab({ costRate, data, isLoading }: Props) {
  const kpis = data?.kpis;
  const extras = data?.extras;
  const dailyFlow = data?.charts?.dailyPlayerFlow ?? [];
  const topAgents = data?.charts?.topAgents ?? [];
  const cash = cashSettlementView(data?.extras?.cashSettlement);

  const activityMix = [
    { name: 'Online deposits', value: kpis?.onlineDepositsNetUsd?.value ?? 0 },
    { name: 'Online withdrawals', value: kpis?.onlineWithdrawalsGrossUsd?.value ?? 0 },
    { name: 'Player money in', value: kpis?.playerMoneyIn?.value ?? 0 },
  ].filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <Section title="Headline">
        <div className="space-y-3">
          <HeadlineCard
            label="Credit margin"
            tooltip="Credits sold minus credit cost for the selected period (your Super Agent trading P&L)."
            kpi={kpis?.netPosition}
            formatValue={formatUsd}
          />
          <MetricStrip
            columnsClassName="grid-cols-2 sm:grid-cols-3 xl:grid-cols-9"
            items={[
              {
                id: 'creditsSold',
                label: MONEY.creditsSold,
                tooltip:
                  'USD earned from credit sales to your Agents (cash + online + balance converts). Same total as on Financials.',
                kpi: kpis?.earnedTotal,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'creditCost',
                label: 'Credit cost',
                tooltip: 'USD you paid the Platform for credits this period.',
                kpi: kpis?.spentTotal,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-orange-100',
                valueClassName: 'text-orange-800',
              },
              {
                id: 'playerMoneyIn',
                label: 'Player money in',
                tooltip: 'Online deposits plus counter cash from Shop-linked Players.',
                kpi: kpis?.playerMoneyIn,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'onlineDep',
                label: 'Online deposits',
                tooltip: 'Net USD from online Player deposits.',
                kpi: kpis?.onlineDepositsNetUsd,
                format: 'currency',
                barClassName: 'bg-emerald-100',
                valueClassName: 'text-emerald-800',
              },
              {
                id: 'onlineWd',
                label: 'Online withdrawals',
                tooltip: 'Gross USD paid out via online withdrawals.',
                kpi: kpis?.onlineWithdrawalsGrossUsd,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-orange-100',
                valueClassName: 'text-orange-800',
              },
              {
                id: 'onlineDebtOutstanding',
                label: MONEY.onlineDebt,
                tooltip:
                  'Live online debt owed to you — same amount as the Roadblocks “Online debt owed to you” row.',
                kpi: kpis?.onlineDebtOutstanding,
                format: 'currency',
                invertDelta: true,
                barClassName: 'bg-rose-100',
                valueClassName: 'text-rose-800',
              },
              {
                id: 'agents',
                label: 'Agents',
                tooltip: 'Agents under your account.',
                value: formatCount(extras?.agentsCount ?? 0),
                barClassName: 'bg-blue-100',
              },
              {
                id: 'shops',
                label: 'Shops',
                tooltip: 'Shops across your Agent network.',
                value: formatCount(extras?.shopsCount ?? 0),
                barClassName: 'bg-blue-100',
              },
              {
                id: 'roadblocks',
                label: 'Roadblocks',
                tooltip: 'Open blockers needing attention.',
                value: formatCount(extras?.roadblocksBadgeCount ?? 0),
                barClassName: extras?.roadblocksBadgeCount ? 'bg-orange-100' : 'bg-emerald-100',
                valueClassName: extras?.roadblocksBadgeCount ? 'text-orange-800' : 'text-emerald-800',
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Wallet">
        <div className="flex flex-col gap-3 rounded-xl border border-blue-100 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Credits</p>
              <p className="text-lg font-semibold tabular-nums text-blue-950">
                {formatCredits(kpis?.creditBalance?.value ?? 0)}
              </p>
              <Link to="/transactions" className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                {PAGE.creditTransactions}
              </Link>
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">USD wallet</p>
              <p className="text-lg font-semibold tabular-nums text-blue-950">
                {formatUsd(kpis?.usdBalanceNet?.value ?? 0)}
              </p>
              <Link to="/online-transactions" className="text-xs font-semibold text-blue-700 hover:text-blue-900">
                {PAGE.usdWalletTransactions}
              </Link>
            </div>
          </div>
          <CreditPurchaseTrigger
            variant="button"
            buttonLayout="stack"
            className="w-full sm:w-auto sm:min-w-[12rem]"
            costRate={costRate}
            creditBalance={kpis?.creditBalance?.value ?? null}
            creditBalanceLoading={isLoading}
            onlineBalanceNet={kpis?.usdBalanceNet?.value ?? null}
            onlineBalanceLoading={isLoading}
            conversionCostRate={costRate}
          />
        </div>
      </Section>

      <Section title="Cash flow">
        <ChartCard
          title="Player cash flow trend"
          tooltip="Player money in (online + counter) vs payouts (online withdrawals + counter redemptions)."
          loading={isLoading}
          empty={!isLoading && dailyFlow.every((d) => d.usdIn === 0 && d.payoutsOut === 0)}
        >
          <div className="h-72">
            <DailySeriesChart
              data={dailyFlow}
              xKey="date"
              kind="bar"
              height={288}
              series={[
                { key: 'usdIn', label: 'Money in', color: CHART_SEMANTIC.moneyIn },
                { key: 'payoutsOut', label: 'Payouts out', color: CHART_SEMANTIC.moneyOut },
              ]}
              valueFormatter={formatUsd}
            />
          </div>
        </ChartCard>
      </Section>

      <Section title="Network">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ChartCard
            title="Top Agents by credits sold"
            tooltip="Agents ranked by credits sold to them in the selected period."
            loading={isLoading}
            empty={!isLoading && topAgents.length === 0}
            emptyLabel="No Agent purchases in this period"
          >
            <CategoryBarChart
              data={topAgents.map((a) => ({ name: a.name, value: a.creditsPurchased }))}
              color={CHART_SEMANTIC.credits}
              valueFormatter={formatUsd}
            />
          </ChartCard>
          <ChartCard
            title="Network activity mix"
            tooltip="Period totals for online deposits, withdrawals, and Player money in."
            loading={isLoading}
            empty={!isLoading && activityMix.length === 0}
            emptyLabel="No network Player activity in this period"
          >
            <CategoryBarChart data={activityMix} color={CHART_SEMANTIC.players} valueFormatter={formatUsd} />
          </ChartCard>
          <ChartCard
            title="Cash settlement health"
            tooltip="Cash your Agents owe you for credit sales this period vs cash already received."
            loading={isLoading}
            empty={!isLoading && cash.expected === 0 && cash.received === 0}
            emptyLabel="No cash settlements in this period"
          >
            <GaugeChart
              percent={cash.collectedPct}
              primaryLabel="Cash received"
              primaryValue={cash.received}
              secondaryLabel="Still uncollected"
              secondaryValue={cash.uncollected}
            />
          </ChartCard>
        </div>
      </Section>
    </div>
  );
}
